import { useCallback } from 'react';
import { ROLES } from '@/constants/users';
import { useAuth } from '@/hooks/useAuth';
import { useUser } from '@/hooks/useUser';

/**
 * SL導線(/sl・?entry=sl)に入ってきた際、AL資格のセッションが残っていたら破棄するためのフック。
 *
 * ANAアプリからSL用URLで流入しても、AL会員としてログイン済みのセッションが継続すると
 * ログインAPIを通らないため membership_type のアップグレード(＋Workhubへの契約作成)が走らない。
 * 一度ログアウトを挟むことでSL会員のログイン導線に乗せる。
 *
 * ログアウトするのは membership_type が ana_lounge と確定できた場合のみ。
 * 取得失敗は判定不能としてセッションを維持する(余計なログアウトを避けるため)。
 *
 * isMembershipUnknown に上限は設けない。上限で「判明した」ことにすると、AL資格のセッションのまま
 * SL導線の外(TOP)へ抜けてしまい、?entry=sl が失われて二度と再判定されないため。
 * 待つあいだ画面を出せない呼び出し側(SuiteLoungeEntrypoint)は、自分の側で待ちを切り上げる。
 *
 * SL導線かどうかの判定は呼び出し側の責務。
 * /sl は URL に entry=sl を持たないため、このフック内では判定できない。
 */
export const useSuiteLoungeSessionReset = () => {
  const { isAuthenticated, isLoaded, role, logout } = useAuth();
  const { isLoading, isAnaLoungeMember } = useUser();

  // 会員種別を持つのは一般ユーザーのみ。スタッフ・共有端末は対象外。
  const isGeneralSession = isAuthenticated && role === ROLES.GENERAL;

  // 認証状態の復元中、または /users/me の結果が出るまでは判定できない
  const isMembershipUnknown = !isLoaded || (isGeneralSession && isLoading);

  const shouldResetSession = isGeneralSession && isAnaLoungeMember;

  const resetSession = useCallback(() => {
    if (!shouldResetSession) return false;

    logout();
    return true;
  }, [shouldResetSession, logout]);

  return {
    isMembershipUnknown,
    shouldResetSession,
    resetSession,
  };
};
